import posthog from 'posthog-js';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

/* eslint sort-keys: "error" */
/** Flag registry; empty whenever no flag is live. @public */
export const FeatureFlag = {} as const;

export type FeatureFlagName = (typeof FeatureFlag)[keyof typeof FeatureFlag];

/**
 * Build feature flag overrides from environment variables.
 * Set FF_{FLAG_NAME}=true in .env.local to override flags locally.
 *
 * @example
 * # .env.local
 * # For FeatureFlag.CRAWL_PDF_SUPPORT:
 * FF_CRAWL_PDF_SUPPORT=true
 */
const buildEnvOverrides = (): Record<string, boolean> => {
  const overrides: Record<string, boolean> = {};

  for (const [key, flagName] of Object.entries<string>(FeatureFlag)) {
    // FF_ prefix is configured in vite.config.ts `envPrefix`.
    // Some defined prefix is required for Vite to expose env vars to client-side code.
    const envKey = `FF_${key}`;
    const envValue = import.meta.env[envKey];
    if (envValue === 'true') {
      overrides[flagName] = true;
    } else if (envValue === 'false') {
      overrides[flagName] = false;
    }
  }

  return overrides;
};

/**
 * Feature Flag Service
 *
 * We use a Service/Provider pattern here to create an abstraction layer over
 * PostHog's feature flag implementation. This allows us to:
 * 1. Swap to a different provider (e.g., LaunchDarkly) by only changing this file
 * 2. Keep all feature flag logic centralized
 * 3. Provide a consistent, type-safe API to consumers
 *
 * @example
 * const featureFlags = useFeatureFlagService();
 *
 * if (featureFlags.isEnabled(FeatureFlag.SOME_CUSTOM_FLAG)) {
 *   return <CustomTagsView />;
 * }
 * return <LegacyView />;
 */
export interface FeatureFlagService {
  isEnabled: (flagName: FeatureFlagName) => boolean;
}

export const FeatureFlagContext = createContext<FeatureFlagService | null>(
  null,
);

export const FeatureFlagProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // PostHog loads feature flags asynchronously after initialization.
  // posthog.onFeatureFlags() fires when flags become available (initial load,
  // after reloadFeatureFlags(), or after identify() calls) - not on every change.
  // We use useReducer to force a re-render when this happens, ensuring consumers
  // see the correct flag values once PostHog has loaded them.
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  // Env overrides bypass PostHog so they work in local dev even when
  // VITE_POSTHOG_KEY is unset (PostHog never initializes, so its override
  // and isFeatureEnabled APIs do nothing).
  const envOverrides = useMemo<Record<string, boolean>>(
    () => (import.meta.env.DEV ? buildEnvOverrides() : {}),
    [],
  );

  useEffect(() => {
    if (Object.keys(envOverrides).length > 0) {
      posthog.featureFlags.override(envOverrides);
    }
  }, [envOverrides]);

  useEffect(() => {
    return posthog.onFeatureFlags(() => {
      forceUpdate();
    });
  }, []);

  const service = useMemo<FeatureFlagService>(
    () => ({
      isEnabled: (flagName) => {
        if (flagName in envOverrides) {
          return envOverrides[flagName];
        }
        return posthog.isFeatureEnabled(flagName) === true;
      },
    }),
    [envOverrides],
  );

  return (
    <FeatureFlagContext.Provider value={service}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

/** Consumer hook for flag checks; unused only while no flag is live. @public */
export const useFeatureFlagService = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error(
      'useFeatureFlagService must be used within a FeatureFlagProvider',
    );
  }
  return context;
};
